import { aktiveKanaele, kanalBeschreibung, kanalPfad, MARKETING_RUBRIKEN, marketingRoute, type Me, type MarketingKanal, type MarketingRoute, type VisibleModule } from '@portal/contracts';
import { onLinkClick } from '../../router.js';
import { Problem } from '../ModulePage.js';
import { Marketing } from './Marketing.js';
import { BERECHTIGTE_LEER, BERECHTIGTE_TITEL, breadcrumb, KACHEL_KENNZAHLEN, kanalNav, LEER_TEXT, rubrikNav } from './marketing-menue.js';
import { datenstand } from './marketing-ui.js';
import './marketing.css';

/**
 * #870 Marketing-Bereich: /marketing (Übersicht = Cockpit Stufe 1, unverändert) · /marketing/veroeffentlichung[/<kanal>] ·
 * /marketing/produktion[/<kanal>]. Unbekannte Pfade/Kanäle → Portal-404. Heute nur Struktur + Platzhalter; Layout passen
 * Laura/Portaldesigner an. Weitere Module des Bereichs (z. B. Benachrichtigungen) hängen sich an die Rubrik-Leiste an.
 */
export function MarketingBereich({ me, pathname }: { me: Me; pathname: string }) {
  const route = marketingRoute(pathname);
  if (!route) return <Problem status={404} />;
  return route.rubrik === 'uebersicht' ? <Marketing me={me} pathname={pathname} /> : <Rubrik route={route} rubrik={route.rubrik} />;
}

/** Unternavigation des Bereichs Marketing (an Stelle der SubNav der Shell): Rubriken + weitere Module des Bereichs */
export function MarketingRubrikNav({ pathname, weitere = [] }: { pathname: string; weitere?: readonly VisibleModule[] }) {
  const route = marketingRoute(pathname) ?? { rubrik: 'uebersicht' as const, kanal: null };
  const unbekannt = !marketingRoute(pathname);
  return (
    <nav className="sh-sub mkt-rubriken" aria-label="Bereich Marketing">
      {rubrikNav(route).map((e) => (
        <a key={e.path} href={e.path} onClick={onLinkClick(e.path)} className={e.aktiv && !unbekannt ? 'is-active' : ''} aria-current={e.aktiv && !unbekannt ? 'page' : undefined}>
          {e.label}
        </a>
      ))}
      {weitere.map((m) => (
        <a key={m.key} href={m.path} onClick={onLinkClick(m.path)}>
          {m.label}
        </a>
      ))}
    </nav>
  );
}

function Rubrik({ route, rubrik }: { route: MarketingRoute; rubrik: 'veroeffentlichung' | 'produktion' }) {
  const r = MARKETING_RUBRIKEN.find((x) => x.key === rubrik)!;
  const kanal = route.kanal;
  return (
    <section className="mod marketing mkt-rubrik">
      <Breadcrumb route={route} />
      <div className="mkt-rubrik__layout">
        <nav className="mkt-kanalnav" aria-label={`Kanäle ${r.label}`}>
          <ul>
            {kanalNav(rubrik, kanal?.slug ?? null).map((e) => (
              <li key={e.path}>
                <a href={e.path} onClick={onLinkClick(e.path)} className={e.aktiv ? 'is-active' : ''} aria-current={e.aktiv ? 'page' : undefined}>
                  {e.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div className="mkt-rubrik__inhalt">
          {kanal ? <KanalSeite rubrik={rubrik} kanal={kanal} /> : <RubrikUebersicht rubrik={rubrik} titel={r.label} beschreibung={r.beschreibung} />}
        </div>
      </div>
    </section>
  );
}

function Breadcrumb({ route }: { route: MarketingRoute }) {
  return (
    <nav className="mkt-breadcrumb" aria-label="Brotkrumen">
      <ol>
        {breadcrumb(route).map((b) => (
          <li key={b.label}>
            {b.path ? <a href={b.path} onClick={onLinkClick(b.path)}>{b.label}</a> : <span aria-current="page">{b.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Leer() {
  return (
    <div className="marketing-leer">
      <p className="marketing-leer__text">{LEER_TEXT}</p>
    </div>
  );
}

function RubrikUebersicht({ rubrik, titel, beschreibung }: { rubrik: 'veroeffentlichung' | 'produktion'; titel: string; beschreibung: string }) {
  return (
    <>
      <h1 className="ap-title">{titel}</h1>
      <p className="marketing-hinweis">{beschreibung}</p>
      <ul className="mkt-kanalkacheln">
        {aktiveKanaele().map((k) => (
          <li key={k.slug} className="pb-card marketing-panel">
            <a className="mkt-kanalkachel__kopf" href={kanalPfad(rubrik, k.slug)} onClick={onLinkClick(kanalPfad(rubrik, k.slug))}>
              <span className="mkt-kuerzel" aria-hidden>{k.kuerzel}</span>
              <h2 className="marketing-panel__titel">{k.name}</h2>
            </a>
            <dl className="mkt-kennzahlen">
              {KACHEL_KENNZAHLEN[rubrik].map((z) => (
                <div key={z}>
                  <dt>{z}</dt>
                  <dd>—</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
      {rubrik === 'produktion' && (
        <section className="pb-card marketing-panel" aria-labelledby="mkt-berechtigte">
          <h2 id="mkt-berechtigte" className="marketing-panel__titel">{BERECHTIGTE_TITEL}</h2>
          <div className="marketing-leer">
            <p className="marketing-leer__text">{BERECHTIGTE_LEER}</p>
          </div>
        </section>
      )}
      <section className="pb-card marketing-panel" aria-label="Inhalte">
        <Leer />
        <p className="marketing-panel__stand">{datenstand(null)}</p>
      </section>
    </>
  );
}

function KanalSeite({ rubrik, kanal }: { rubrik: 'veroeffentlichung' | 'produktion'; kanal: MarketingKanal }) {
  return (
    <>
      <h1 className="ap-title">{kanal.name}</h1>
      <p className="marketing-hinweis">{kanalBeschreibung(rubrik, kanal)}</p>
      <section className="pb-card marketing-panel" aria-label={`Inhalte ${kanal.name}`}>
        <Leer />
        <p className="marketing-panel__stand">{datenstand(null)}</p>
      </section>
    </>
  );
}
